import { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { lightTheme } from '../theme';
import { useLanguage } from '../contexts/LanguageContext';
import MiniAppPreview from './Shared/MiniAppPreview';

function ThemeManagement({ theme, onThemeChange, onBrandingChange }) {
    const { t } = useLanguage();
    const [themes, setThemes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingTheme, setEditingTheme] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [defaultThemeMode, setDefaultThemeMode] = useState('dark');
    const [formData, setFormData] = useState({
        name: '',
        theme_data: {
            colors: {
                primary: lightTheme.colors.primary,
                secondary: lightTheme.colors.secondary,
                background: lightTheme.colors.background,
                surface: lightTheme.colors.surface,
                error: lightTheme.colors.error,
                text: lightTheme.colors.text,
                textLight: lightTheme.colors.textLight,
                textSecondary: lightTheme.colors.textSecondary || '#666666ff',
                border: lightTheme.colors.border,
                success: lightTheme.colors.success,
                warning: lightTheme.colors.warning
            },
            fonts: {
                main: lightTheme.fonts.main,
                heading: lightTheme.fonts.heading
            },
            borderRadius: lightTheme.borderRadius,
            boxShadow: lightTheme.boxShadow
        },
        active: 1
    });

    const fetchThemes = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BACKEND_URL}/api/themes`);
            if (!response.ok) throw new Error(t('failedToFetchThemes'));
            const data = await response.json();
            setThemes(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [t]);

    const fetchBranding = useCallback(async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/branding`);
            if (!response.ok) return;
            const data = await response.json();
            if (data.logoUrl === '__uploaded__') {
                setCurrentLogoUrl(`${BACKEND_URL}/api/branding/logo?t=${Date.now()}`);
            } else if (data.logoUrl) {
                setCurrentLogoUrl(data.logoUrl);
            } else {
                setCurrentLogoUrl(null);
            }
            if (data.defaultThemeMode) {
                setDefaultThemeMode(data.defaultThemeMode);
            }
        } catch {
            // ignore
        }
    }, []);

    useEffect(() => {
        fetchThemes();
        fetchBranding();
    }, [fetchThemes, fetchBranding]);

    const handleSetDefault = async (themeId, type) => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/themes/${themeId}/set-default`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type })
            });
            if (!response.ok) throw new Error(t('failedToSaveTheme'));
            await fetchThemes();
            if (onBrandingChange) onBrandingChange();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleLogoUpload = async () => {
        if (!logoFile) return;
        setUploadingLogo(true);
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('logo', logoFile);
            const response = await fetch(`${BACKEND_URL}/api/branding/logo`, {
                method: 'POST',
                body: formDataUpload
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || t('error'));
            }
            setLogoFile(null);
            setLogoPreview(null);
            await fetchBranding();
            if (onBrandingChange) onBrandingChange();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleLogoDelete = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/branding/logo`, { method: 'DELETE' });
            if (!response.ok) throw new Error(t('error'));
            setCurrentLogoUrl(null);
            setLogoFile(null);
            setLogoPreview(null);
            if (onBrandingChange) onBrandingChange();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleDefaultModeChange = async (mode) => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/system-settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ defaultThemeMode: mode })
            });
            if (!response.ok) throw new Error(t('error'));
            setDefaultThemeMode(mode);
            if (onBrandingChange) onBrandingChange();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleLogoFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            alert(t('error') + ': Max 2MB');
            return;
        }
        setLogoFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setLogoPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingTheme
                ? `${BACKEND_URL}/api/themes/${editingTheme.id}`
                : `${BACKEND_URL}/api/themes`;
            const method = editingTheme ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (!response.ok) throw new Error(t('failedToSaveTheme'));

            await fetchThemes();
            resetForm();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleEdit = (themeToEdit) => {
        setEditingTheme(themeToEdit);
        setFormData({
            name: themeToEdit.name,
            theme_data: { ...themeToEdit.theme_data },
            active: themeToEdit.active
        });
        setShowAddForm(true);
    };

    const handleDelete = async (id) => {
        const themeToDelete = themes.find(t => t.id === id);
        if (themeToDelete?.is_default || themeToDelete?.is_default_light || themeToDelete?.is_default_dark) {
            alert(t('cannotDeleteDefaultThemes'));
            return;
        }

        if (!window.confirm(t('confirmDeleteTheme'))) return;

        try {
            const response = await fetch(`${BACKEND_URL}/api/themes/${id}`, {
                method: 'DELETE'
            });
            if (!response.ok) throw new Error(t('failedToDeleteTheme'));
            await fetchThemes();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleApplyTheme = (selectedTheme) => {
        if (onThemeChange) {
            onThemeChange(selectedTheme);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            theme_data: {
                colors: {
                    primary: lightTheme.colors.primary,
                    secondary: lightTheme.colors.secondary,
                    background: lightTheme.colors.background,
                    surface: lightTheme.colors.surface,
                    error: lightTheme.colors.error,
                    text: lightTheme.colors.text,
                    textLight: lightTheme.colors.textLight,
                    textSecondary: lightTheme.colors.textSecondary || '#666',
                    border: lightTheme.colors.border,
                    success: lightTheme.colors.success,
                    warning: lightTheme.colors.warning
                },
                fonts: {
                    main: lightTheme.fonts.main,
                    heading: lightTheme.fonts.heading
                },
                borderRadius: lightTheme.borderRadius,
                boxShadow: lightTheme.boxShadow
            },
            active: 1
        });
        setEditingTheme(null);
        setShowAddForm(false);
    };

    if (loading) return <div>{t('loadingThemes')}...</div>;
    if (error) return <div style={{ color: 'red' }}>{t('error')}: {error}</div>;

    return (
        <div style={{
            background: theme.colors.surface,
            padding: '0rem',
            borderRadius: theme.borderRadius || 8,
            boxShadow: theme.boxShadow,
            marginBottom: '1.5rem'
        }}>
            {/* Logo Upload Section */}
            <div style={{
                background: theme.colors.background,
                padding: '1rem',
                borderRadius: theme.borderRadius || 8,
                marginBottom: '1rem',
                border: `1px solid ${theme.colors.border}`
            }}>
                <h3 style={{ color: theme.colors.primary, margin: '0 0 0.75rem 0', fontSize: '1rem' }}>
                    {t('logo') || 'Logo'}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    {(logoPreview || currentLogoUrl) && (
                        <img
                            src={logoPreview || currentLogoUrl}
                            alt="Logo"
                            style={{
                                maxHeight: '60px',
                                maxWidth: '200px',
                                objectFit: 'contain',
                                background: theme.colors.surface,
                                padding: '0.25rem',
                                borderRadius: '4px',
                                border: `1px solid ${theme.colors.border}`
                            }}
                        />
                    )}
                    {!currentLogoUrl && !logoPreview && (
                        <span style={{ color: theme.colors.textSecondary, fontSize: '0.85rem' }}>
                            {t('usingDefaultLogo') || 'Using default BOCA logo'}
                        </span>
                    )}
                    <input
                        type="file"
                        accept=".svg,.png,.jpg,.jpeg"
                        onChange={handleLogoFileChange}
                        style={{ fontSize: '0.85rem' }}
                    />
                    <span style={{ fontSize: '0.78rem', color: theme.colors.textSecondary || '#888' }}>
                        Note: This logo is shared across the entire app and the public menu page.
                    </span>
                    {logoFile && (
                        <button
                            onClick={handleLogoUpload}
                            disabled={uploadingLogo}
                            style={{
                                padding: '0.4rem 0.8rem',
                                backgroundColor: theme.colors.success || '#27ae60',
                                color: theme.colors.textLight || '#fff',
                                border: 'none',
                                borderRadius: theme.borderRadius || 8,
                                cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {uploadingLogo ? '...' : (t('uploadLogo') || 'Upload')}
                        </button>
                    )}
                    {currentLogoUrl && (
                        <button
                            onClick={handleLogoDelete}
                            style={{
                                padding: '0.4rem 0.8rem',
                                backgroundColor: theme.colors.error || '#e74c3c',
                                color: theme.colors.textLight || '#fff',
                                border: 'none',
                                borderRadius: theme.borderRadius || 8,
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {t('resetLogo') || 'Reset to Default'}
                        </button>
                    )}
                </div>
            </div>

            {/* Default Startup Mode */}
            <div style={{
                background: theme.colors.background,
                padding: '1rem',
                borderRadius: theme.borderRadius || 8,
                marginBottom: '1rem',
                border: `1px solid ${theme.colors.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
            }}>
                <h3 style={{ color: theme.colors.primary, margin: 0, fontSize: '1rem' }}>
                    {t('defaultStartupMode') || 'Default Startup Mode'}
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                        onClick={() => handleDefaultModeChange('light')}
                        style={{
                            padding: '0.4rem 0.8rem',
                            backgroundColor: defaultThemeMode === 'light' ? (theme.colors.primary) : 'transparent',
                            color: defaultThemeMode === 'light' ? (theme.colors.textLight || '#fff') : theme.colors.text,
                            border: `1px solid ${theme.colors.primary}`,
                            borderRadius: theme.borderRadius || 8,
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: defaultThemeMode === 'light' ? 'bold' : 'normal'
                        }}
                    >
                        {'☀️'} {t('light') || 'Light'}
                    </button>
                    <button
                        onClick={() => handleDefaultModeChange('dark')}
                        style={{
                            padding: '0.4rem 0.8rem',
                            backgroundColor: defaultThemeMode === 'dark' ? (theme.colors.primary) : 'transparent',
                            color: defaultThemeMode === 'dark' ? (theme.colors.textLight || '#fff') : theme.colors.text,
                            border: `1px solid ${theme.colors.primary}`,
                            borderRadius: theme.borderRadius || 8,
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: defaultThemeMode === 'dark' ? 'bold' : 'normal'
                        }}
                    >
                        {'🌙'} {t('dark') || 'Dark'}
                    </button>
                </div>
            </div>

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
            }}>
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: theme.colors.primary,
                        color: theme.colors.surface,
                        border: 'none',
                        borderRadius: theme.borderRadius || 8,
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        boxShadow: theme.boxShadow
                    }}
                >
                    {showAddForm ? t('cancel') : t('addTheme')}
                </button>
            </div>

            {showAddForm && (
                <form onSubmit={handleSubmit} style={{
                    background: theme.colors.background,
                    padding: '1rem',
                    borderRadius: theme.borderRadius || 8,
                    marginBottom: '1rem',
                    border: `1px solid ${theme.colors.primary}20`
                }}>
                    <h3 style={{ color: theme.colors.primary, margin: '0 0 1rem 0' }}>
                        {editingTheme ? t('editTheme') : t('addNewTheme')}
                    </h3>

                    {/* Top Button Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '1rem',
                        marginBottom: '1rem'
                    }}>
                        <button
                            type="button"
                            onClick={resetForm}
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: theme?.colors?.error || '#f44336',
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 8,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={!formData.name}
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: !formData.name ? '#ccc' : (theme?.colors?.success || '#4caf50'),
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 8,
                                cursor: !formData.name ? 'not-allowed' : 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                opacity: !formData.name ? 0.6 : 1
                            }}
                        >
                            {editingTheme ? t('update') : t('add')} {t('theme')}
                        </button>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            {t('themeName')}:
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            required
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                borderRadius: theme.borderRadius || 4,
                                border: `1px solid ${theme.colors.primary}40`
                            }}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('primaryColor')}:
                            </label>
                            <input
                                type="color"
                                value={formData.theme_data.colors.primary}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    theme_data: {
                                        ...formData.theme_data,
                                        colors: {...formData.theme_data.colors, primary: e.target.value}
                                    }
                                })}
                                style={{ width: '100%', height: '40px', borderRadius: theme.borderRadius || 4 }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('secondaryColor')}:
                            </label>
                            <input
                                type="color"
                                value={formData.theme_data.colors.secondary}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    theme_data: {
                                        ...formData.theme_data,
                                        colors: {...formData.theme_data.colors, secondary: e.target.value}
                                    }
                                })}
                                style={{ width: '100%', height: '40px', borderRadius: theme.borderRadius || 4 }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('backgroundColor')}:
                            </label>
                            <input
                                type="color"
                                value={formData.theme_data.colors.background}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    theme_data: {
                                        ...formData.theme_data,
                                        colors: {...formData.theme_data.colors, background: e.target.value}
                                    }
                                })}
                                style={{ width: '100%', height: '40px', borderRadius: theme.borderRadius || 4 }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('surfaceColor')}:
                            </label>
                            <input
                                type="color"
                                value={formData.theme_data.colors.surface}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    theme_data: {
                                        ...formData.theme_data,
                                        colors: {...formData.theme_data.colors, surface: e.target.value}
                                    }
                                })}
                                style={{ width: '100%', height: '40px', borderRadius: theme.borderRadius || 4 }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('textColor')}:
                            </label>
                            <input
                                type="color"
                                value={formData.theme_data.colors.text}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    theme_data: {
                                        ...formData.theme_data,
                                        colors: {...formData.theme_data.colors, text: e.target.value}
                                    }
                                })}
                                style={{ width: '100%', height: '40px', borderRadius: theme.borderRadius || 4 }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('textLightColor')}:
                            </label>
                            <input
                                type="color"
                                value={formData.theme_data.colors.textLight}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    theme_data: {
                                        ...formData.theme_data,
                                        colors: {...formData.theme_data.colors, textLight: e.target.value}
                                    }
                                })}
                                style={{ width: '100%', height: '40px', borderRadius: theme.borderRadius || 4 }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('textSecondaryColor')}:
                            </label>
                            <input
                                type="color"
                                value={formData.theme_data.colors.textSecondary}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    theme_data: {
                                        ...formData.theme_data,
                                        colors: {...formData.theme_data.colors, textSecondary: e.target.value}
                                    }
                                })}
                                style={{ width: '100%', height: '40px', borderRadius: theme.borderRadius || 4 }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('borderColor')}:
                            </label>
                            <input
                                type="color"
                                value={formData.theme_data.colors.border}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    theme_data: {
                                        ...formData.theme_data,
                                        colors: {...formData.theme_data.colors, border: e.target.value}
                                    }
                                })}
                                style={{ width: '100%', height: '40px', borderRadius: theme.borderRadius || 4 }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('errorColor')}:
                            </label>
                            <input
                                type="color"
                                value={formData.theme_data.colors.error}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    theme_data: {
                                        ...formData.theme_data,
                                        colors: {...formData.theme_data.colors, error: e.target.value}
                                    }
                                })}
                                style={{ width: '100%', height: '40px', borderRadius: theme.borderRadius || 4 }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('successColor')}:
                            </label>
                            <input
                                type="color"
                                value={formData.theme_data.colors.success}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    theme_data: {
                                        ...formData.theme_data,
                                        colors: {...formData.theme_data.colors, success: e.target.value}
                                    }
                                })}
                                style={{ width: '100%', height: '40px', borderRadius: theme.borderRadius || 4 }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('warningColor')}:
                            </label>
                            <input
                                type="color"
                                value={formData.theme_data.colors.warning}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    theme_data: {
                                        ...formData.theme_data,
                                        colors: {...formData.theme_data.colors, warning: e.target.value}
                                    }
                                })}
                                style={{ width: '100%', height: '40px', borderRadius: theme.borderRadius || 4 }}
                            />
                        </div>

                        {/* <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                Border Radius:
                            </label>
                            <input
                                type="text"
                                value={formData.theme_data.borderRadius}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    theme_data: {
                                        ...formData.theme_data,
                                        borderRadius: e.target.value
                                    }
                                })}
                                placeholder="8px"
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: `1px solid ${theme.colors.primary}40`
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                Box Shadow:
                            </label>
                            <input
                                type="text"
                                value={formData.theme_data.boxShadow}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    theme_data: {
                                        ...formData.theme_data,
                                        boxShadow: e.target.value
                                    }
                                })}
                                placeholder="0 2px 8px rgba(0,0,0,0.08)"
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: `1px solid ${theme.colors.primary}40`
                                }}
                            />
                        </div> */}
                    </div>

                    {/* Theme Preview Section */}
                    <div style={{
                        marginBottom: '1rem',
                        padding: '1rem',
                        background: formData.theme_data.colors.background,
                        border: `1px solid ${formData.theme_data.colors.border}`,
                        borderRadius: formData.theme_data.borderRadius,
                        boxShadow: formData.theme_data.boxShadow
                    }}>
                        <h3 style={{ 
                            color: formData.theme_data.colors.primary, 
                            margin: '0 0 0.75rem 0',
                            fontSize: '1.1rem'
                        }}>
                            {t('themePreview')}
                        </h3>
                        <MiniAppPreview theme={formData.theme_data} />
                    </div>

                    {/* Bottom Button Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '1rem'
                    }}>
                        <button
                            type="button"
                            onClick={resetForm}
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: theme?.colors?.error || '#f44336',
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 8,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={!formData.name}
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: !formData.name ? '#ccc' : (theme?.colors?.success || '#4caf50'),
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 8,
                                cursor: !formData.name ? 'not-allowed' : 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                opacity: !formData.name ? 0.6 : 1
                            }}
                        >
                            {editingTheme ? t('update') : t('add')} {t('theme')}
                        </button>
                    </div>
                </form>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
                {themes.map(themeItem => (
                    <div key={themeItem.id} style={{
                        background: themeItem.theme_data.colors.background,
                        border: `2px solid ${themeItem.theme_data.colors.primary}`,
                        borderRadius: themeItem.theme_data.borderRadius,
                        padding: '1rem',
                        boxShadow: themeItem.theme_data.boxShadow
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '0.5rem'
                        }}>
                            <h3 style={{ color: themeItem.theme_data.colors.primary, margin: 0 }}>
                                {themeItem.name}
                            </h3>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                                {themeItem.is_default_light === 1 && (
                                    <span style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem', borderRadius: '4px', background: '#fff3cd', color: '#856404' }}>
                                        {t('defaultLight') || 'Default Light'}
                                    </span>
                                )}
                                {themeItem.is_default_dark === 1 && (
                                    <span style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem', borderRadius: '4px', background: '#343a40', color: '#f8f9fa' }}>
                                        {t('defaultDark') || 'Default Dark'}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Mini App Preview */}
                        <div style={{ 
                            marginBottom: '1rem',
                            height: '200px',
                            overflow: 'hidden'
                        }}>
                            <MiniAppPreview theme={themeItem.theme_data} />
                        </div>
                        
                        {/* Color Swatches */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: themeItem.theme_data.colors.primary,
                                borderRadius: '4px',
                                title: t('primary')
                            }}></div>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: themeItem.theme_data.colors.secondary,
                                borderRadius: '4px',
                                title: t('secondary')
                            }}></div>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: themeItem.theme_data.colors.background,
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                title: t('background')
                            }}></div>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: themeItem.theme_data.colors.surface,
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                title: t('surface')
                            }}></div>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: themeItem.theme_data.colors.text,
                                borderRadius: '4px',
                                title: t('text')
                            }}></div>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: themeItem.theme_data.colors.textLight,
                                borderRadius: '4px',
                                title: t('textLight')
                            }}></div>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: themeItem.theme_data.colors.textSecondary,
                                borderRadius: '4px',
                                title: t('textSecondary')
                            }}></div>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: themeItem.theme_data.colors.border,
                                borderRadius: '4px',
                                border: '1px solid  #ccc',
                                title: t('border')
                            }}></div>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: themeItem.theme_data.colors.error,
                                borderRadius: '4px',
                                title: t('error')
                            }}></div>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: themeItem.theme_data.colors.success,
                                borderRadius: '4px',
                                title: t('success')
                            }}></div>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: themeItem.theme_data.colors.warning,
                                borderRadius: '4px',
                                title: t('warning')
                            }}></div>
                        </div>

                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button
                                onClick={() => handleApplyTheme(themeItem.theme_data)}
                                style={{
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: themeItem.theme_data.colors.primary,
                                    color: themeItem.theme_data.colors.textLight,
                                    border: 'none',
                                    borderRadius: themeItem.theme_data.borderRadius,
                                    cursor: 'pointer',
                                    fontSize: '0.8rem'
                                }}
                            >
                                {t('apply')}
                            </button>
                            {!themeItem.is_default_light && (
                                <button
                                    onClick={() => handleSetDefault(themeItem.id, 'light')}
                                    title={t('setAsDefaultLight') || 'Set as default light theme'}
                                    style={{
                                        padding: '0.25rem 0.5rem',
                                        backgroundColor: '#fff3cd',
                                        color: '#856404',
                                        border: '1px solid #ffc107',
                                        borderRadius: themeItem.theme_data.borderRadius,
                                        cursor: 'pointer',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    {t('setLight') || 'Set Light'}
                                </button>
                            )}
                            {!themeItem.is_default_dark && (
                                <button
                                    onClick={() => handleSetDefault(themeItem.id, 'dark')}
                                    title={t('setAsDefaultDark') || 'Set as default dark theme'}
                                    style={{
                                        padding: '0.25rem 0.5rem',
                                        backgroundColor: '#343a40',
                                        color: '#f8f9fa',
                                        border: '1px solid #6c757d',
                                        borderRadius: themeItem.theme_data.borderRadius,
                                        cursor: 'pointer',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    {t('setDark') || 'Set Dark'}
                                </button>
                            )}
                            {!themeItem.is_default && !themeItem.is_default_light && !themeItem.is_default_dark && (
                                <>
                                    <button
                                        onClick={() => handleEdit(themeItem)}
                                        style={{
                                            padding: '0.25rem 0.5rem',
                                            backgroundColor: themeItem.theme_data.colors.secondary,
                                            color: themeItem.theme_data.colors.textLight,
                                            border: 'none',
                                            borderRadius: themeItem.theme_data.borderRadius,
                                            cursor: 'pointer',
                                            fontSize: '0.8rem'
                                        }}
                                    >
                                        {t('edit')}
                                    </button>
                                    <button
                                        onClick={() => handleDelete(themeItem.id)}
                                        style={{
                                            padding: '0.25rem 0.5rem',
                                            backgroundColor: themeItem.theme_data.colors.error,
                                            color: themeItem.theme_data.colors.textLight,
                                            border: 'none',
                                            borderRadius: themeItem.theme_data.borderRadius,
                                            cursor: 'pointer',
                                            fontSize: '0.8rem'
                                        }}
                                    >
                                        {t('delete')}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default ThemeManagement;